// **LIVE-1 — GOING LIVE IN THE LIVING ROOM: THE LAWS, DECLARED ONCE.**
//
// HER SPEC, in her own words: *"Vet plus owners, Cloudflare Stream, Go Live in the Living Room, live
// circles, no replays, moderation rail, her Cloudflare steps named when needed."*
//
// This module imports NOTHING, which is the entry requirement that lets a FAST fixture LOAD it and
// RUN every outcome with no Cloudflare account, no database and no network. The transport lives in
// `server/stream-live.js` and the WIRING is asked separately, because a perfect law nothing calls is
// not a law — which is the defect this house has just spent a whole round measuring one gate over.

// >>> **WHO MAY GO LIVE IS THE FEATURE TABLE'S ANSWER, NEVER A TIER NAME TYPED HERE.** <<<
// `FEATURES["room:live"]` in `server/tiers.js` is the ONE place her *Vet* clause is written down, so
// moving going-live between tiers is one line — exactly as `room:living` and `room:magic` already
// are. A law here that compared against the string `"vet"` would be a second answer to what a tier
// grants (R28/R29), and the copy that drifts decides whether a member she promoted can go live.
export const LIVE_FEATURE = "room:live";

// >>> **AND HER "PLUS OWNERS" NEEDS NO LAW AT ALL, WHICH THIS ROUND FOUND BY TRYING TO WRITE ONE.**
//
// The first draft of this module exported `mayGoLive({ featureOpen, owner })` — two facts asked as
// two (R18), fail-closed, presence rather than truthiness, and **reachable from nothing.**
// `resolveEntitlements` opens with `if (member.owner) return entitlementsFor({ owner: true })`, so
// `requireFeature(LIVE_FEATURE)` ALREADY answers both halves of her sentence: the Vet half off the
// table above, the owner half off that line. A law beside it could only ever be handed
// `featureOpen: true` by a route that had just proved it — a tautology, which is a line no reader
// can trust (R216) and a capability this house would then claim to have and not use (R38/R165's
// trap, which this file has just spent a round measuring one gate over).
//
// **SO IT IS DELETED AT ITS DECLARATION RATHER THAN WIRED FOR THE SAKE OF BEING CALLED**, and the
// reasoning is kept here because the next reader's instinct will be to write it again.

// ---------------------------------------------------------------------------------------------------
// >>> **HER "NO REPLAYS" IS STRUCTURAL RATHER THAN A PROMISE, AND THAT IS THE ROUND'S ONE FREE WIN.**
//
// Cloudflare's live input takes `recording.mode`, and `"off"` is a DOCUMENTED allowed value
// (verified against `/stream/stream-live/start-stream-live/` rather than remembered — rule 1: never
// invent an endpoint or a field). With it off **no recording is ever made**, so there is nothing to
// delete, nothing to leak and nothing to store: LIVE-1's own costing measured storage at $5 per
// 1,000 minutes per MONTH, recurring forever, and this makes that line **$0 by construction** rather
// than by somebody remembering to run a sweep.
//
// A build that recorded and then deleted satisfies *no replays* in every diff and leaves a window in
// which her members' broadcasts exist on a third party's disk. One declaration, read by the
// transport, so the two cannot drift.
export const NO_REPLAY = "off";
export function liveRecordingSetting() {
  return { mode: NO_REPLAY };
}

// ---------------------------------------------------------------------------------------------------
// >>> **THREE STATES, AND THE THIRD IS THE ONE A NAIVE BUILD LOSES (R18).**
//
// `live` and `ended` are the obvious pair, and between them sits the case that really happens: a
// host whose laptop shut, whose signal dropped or who simply closed the tab **never sends the end**.
// With two states her broadcast shows as LIVE in the Living Room for ever, members tap in to
// silence, and nothing anywhere is red — the failure this house calls silent (R8-4).
//
// So a session is live only while it has been HEARD FROM recently. `LIVE_HEARTBEAT_MS` is how often
// the host's own page says it is still there and `LIVE_STALE_MS` is how long the room waits before
// calling it over — a band rather than a number, because a floor alone is satisfied by a heartbeat
// nobody sends and a ceiling alone by a session that is never allowed to end.
export const LIVE_HEARTBEAT_MS = 20_000;
export const LIVE_STALE_MS = 90_000;

/**
 * `row.endedAt` is the host saying so; anything else is decided by the clock.
 *
 * An unreadable stamp is STALE rather than live, which is the same fail-closed direction as the door
 * above: a stale verdict costs a live host one tap to start again, and a live verdict on a dead
 * session costs every member who taps into it.
 */
export function liveSessionState(row, nowMs) {
  if (!row || typeof row !== "object") return "ended";
  if (row.endedAt) return "ended";
  const seen = Date.parse(row.lastSeenAt || row.startedAt || "");
  const now = Number.isFinite(nowMs) ? nowMs : NaN;
  if (!Number.isFinite(seen) || !Number.isFinite(now)) return "stale";
  return now - seen <= LIVE_STALE_MS ? "live" : "stale";
}

/** What the room draws. A stale session is over as far as a member is concerned (R18: two facts, one screen). */
export function sessionIsWatchable(row, nowMs) {
  return liveSessionState(row, nowMs) === "live";
}

// ---------------------------------------------------------------------------------------------------
// >>> **HER "LIVE CIRCLES", AND THE ONE WORD IN HER SPEC THAT NEEDED A DECISION (DECISIONS LIVE-1-C1).**
//
// A circle is a **GROUP THREAD'S MEMBERSHIP**, or the whole room. That is the conservative reading
// and it is measured rather than chosen: a group thread is the only thing in this house already
// shaped like a circle with a membership list, a name, her moderation and LR1 ruling 2's absolute
// blocking — so a live circle reuses all four rather than minting a fifth roster to keep in step
// (R28/R29). The FOLLOW graph was the other candidate and **LIVE-1's own B6 measured it too thin to
// be a gate**: 913 edges over 2,186 members, a median of one follower, and the biggest non-owner
// account at 45.
//
// **THE DEFAULT IS THE NARROW ONE, AND THAT DIRECTION IS THE RULING (R19).** Showing a member's live
// face to a wider audience than she meant is the failure that cannot be taken back; showing it to a
// narrower one costs her viewers she can invite again. So an unrostered scope, a missing scope and a
// malformed one all resolve to the CIRCLE rather than to the house.
// >>> HER RULING 2026-08-26: THREE AUDIENCES, IN HER OWN WORDS. <<<
//
// "People who follow me", "A group I created", "Everyone in the Living Room". The roster MOVED
// rather than gaining a parallel one, because this value is now doing three jobs at once -- it is
// the notification list, the gate on the watch door, and who the circle at the top of the room is
// drawn for. Two rosters would be two answers to one question (R28/R29), and the one that drifted
// would either call people who cannot get in or gate out people it had just invited.
//
// **`circle` IS `group` AND IS ACCEPTED FOR EVER**, because rows written before this ruling carry
// it. Migration 131 renames what is on disk; this reads the old spelling anyway, since a legacy
// value arriving from anywhere must never fall through to a DIFFERENT audience than it meant.
//
// **DECISIONS LIVE-1-C1 IS SUPERSEDED BY HER OWN WORD.** That ruling measured the follow graph too
// thin to be a gate and chose the narrow reading; she has now named it as an option outright. Thin
// is not wrong: a broadcaster with one follower calling that one follower is what she asked for.
export const LIVE_SCOPES = ["followers", "group", "house"];

// **THE DEFAULT IS STILL THE NARROW ONE AND THAT DIRECTION IS STILL THE RULING (R19).** Showing a
// member's live face to a wider audience than she meant cannot be taken back; a narrower one costs
// viewers she can invite again. Of the three, her followers are the narrowest she can always fill.
export const DEFAULT_LIVE_SCOPE = "followers";

const LEGACY_SCOPES = { circle: "group" };

export function liveScopeOf(raw) {
  const named = LEGACY_SCOPES[raw] || raw;
  return LIVE_SCOPES.includes(named) ? named : DEFAULT_LIVE_SCOPE;
}

// Her labels, declared once so the picker, the refusal sentence and the notification cannot drift.
export const LIVE_SCOPE_LABELS = Object.freeze({
  followers: "People who follow me",
  group: "A group I created",
  house: "Everyone in the Living Room",
});

// A group audience is the ONLY one that needs a second fact, so it is the only one asked for it.
export function scopeNeedsGroup(scope) {
  return liveScopeOf(scope) === "group";
}

/**
 * MAY THIS VIEWER WATCH?
 *
 * `house` is every member the Living Room already admits — this law adds no audience of its own, so
 * the room's own tier gate is still the wall. `circle` additionally requires her to be IN the
 * thread the host opened it to.
 *
 * **BLOCKING OUTRANKS BOTH, AND IT IS ASKED FIRST** (LR1 ruling 2: absolute and symmetric). A member
 * the host has blocked, or who has blocked the host, is not admitted to a live broadcast by either
 * scope — and the answer is the same `false` a non-member gets, because telling her she is blocked
 * hands her exactly what a block exists to withhold.
 */
// >>> MAY THIS VIEWER WATCH? THREE AUDIENCES, ONE ANSWER. <<<
//
// `house` is every member the Living Room already admits -- this law adds no audience of its own.
// `followers` requires her to follow the host. `group` requires her to be IN the group the host
// chose, and that is the one her ruling calls GATED: server-side, on the join, not merely a
// notification list somebody could tap past.
//
// **BLOCKING OUTRANKS ALL THREE AND IS ASKED FIRST** (LR1 ruling 2: absolute and symmetric), and
// the answer is the same `false` a stranger gets, because telling her she is blocked hands her
// exactly what a block exists to withhold.
//
// **THE MEMBERSHIP FACT IS PASSED IN RATHER THAN LOOKED UP**, so this stays importable by a gate
// with no database -- and it is PRESENCE, never truthiness (R53/R136/R137): a missing answer is
// not admittance.
export function circleAdmits({ scope, blocked, inCircle, follows } = {}) {
  if (blocked === true) return false;
  const named = liveScopeOf(scope);
  if (named === "house") return true;
  if (named === "followers") return follows === true;
  return inCircle === true;
}

// ---------------------------------------------------------------------------------------------------
// >>> **THE MODERATION RAIL: HER SEAT ENDS A BROADCAST, AND THAT IS A DIFFERENT ACT FROM THE HOST
// ENDING IT (R18).**
//
// Both stop the stream and only one of them is hers. Collapsing them loses the one thing her ledger
// is for — *who stopped this, and why* — on the one surface where a member was speaking live to a
// room. `LIVE_END_REASONS` is the roster; an unrostered reason ends NOTHING rather than writing an
// unaccountable row onto an append-only ledger (C3's own ruling, on a table with a writer, a reader
// and no editor).
export const LIVE_END_REASONS = ["host", "owner", "stale"];

export function endReasonValid(reason) {
  return LIVE_END_REASONS.includes(reason);
}

/**
 * WHO MAY END IT. The host may end her own; the house may end anybody's; nobody may end somebody
 * else's. `stale` is the CLOCK's answer and is never a person's, so it is refused here — a caller
 * claiming it would be writing that the signal dropped when a thumb pressed a button (R141).
 */
export function mayEndSession({ reason, owner, isHost } = {}) {
  if (reason === "owner") return owner === true;
  if (reason === "host") return isHost === true;
  return false;
}
